import React, { useEffect, useState, useContext } from 'react'
import { UserContext } from './UserContext'
import { BsChevronExpand, BsChevronDown, BsChevronUp } from "react-icons/bs"
import Tippy from '@tippyjs/react'
import './styles/TableBox.css'
import jwt_decode from 'jwt-decode'

const TableBox = ({
  table_data,
  handlerRowClicked,
  handlerKeyDown,
  rowIndexClicked,
  SetRowIndexClicked,
  filterText,
  oppListExpanded,
  tooltipSW,
  SetOppTableLength,
  showSR2,
  showAciveOpps,
  upgradeMessage,
  promotionMessage,
  promotionCouponCode,
  promotionBackColor,
  showUpgradeBanner,
  SetShowUpgradeBanner,
  SetNumLongs,
  SetNumShorts
}) => {

  const { tableTextSize, tableTitleTextSize, wpUserLevels, loggedinUser, token, rdd } = useContext(UserContext)

  const [tableDataProcessed, SetTableDataProcessed] = useState(table_data)
  const [colSorted, SetColSorted] = useState('sharpe_ratio')
  const [sortedDir, SetSortedDir] = useState('d') // ascending or descending
  const [tableTitleDict, SetTableTitleDict] = useState({})
  const [tableTitleTooltip, SetTableTitleTooltip] = useState({})

  let rowHeight = '3vh'
  if (rdd.isMobile && !rdd.isTablet && window.innerHeight > window.innerWidth) {
    // smartphone portrait
  } else if (rdd.isMobile && !rdd.isTablet && window.innerHeight < window.innerWidth) {
    // smartphone landscape
    rowHeight = '7vh'
  } else if (rdd.isMobile && rdd.isTablet && window.innerHeight > window.innerWidth) {
    // tablet portrait
  } else if (rdd.isMobile && rdd.isTablet && window.innerHeight < window.innerWidth) {
    // tablet landscape
  } else if (!rdd.isMobile) {
    // desktop
  }

  // Not generic, used as a second column for tie-breaks
  const secondColSort = 'date'

  useEffect(() => {
    // Copy the original table data
    let tmp = JSON.parse(JSON.stringify(table_data))

    // If there is any filter text, split it into segments by semicolon
    if (filterText && filterText.trim() !== "") {
      const filters = filterText
        .split(';')
        .map(segment => segment.trim())
        .filter(segment => segment.length > 0)

      // Process each filter segment (they are ANDed together)
      filters.forEach(segment => {
        // Convert segment to lowercase for matching, or use /i in the regex
        const lowerSegment = segment.toLowerCase()

        // Sharpe Ratio filter: e.g., "SR>2"
        if (/^sr\s*>\s*(\d+(\.\d+)?)/i.test(segment)) {
          const match = segment.match(/^sr\s*>\s*(\d+(\.\d+)?)/i)
          const threshold = parseFloat(match[1])
          tmp = tmp.filter(item => item.sharpe_ratio >= threshold)
        }
        // Average Profit filter: e.g., "AP>10" or "AVGP>10"
        // both mean filter item.avg_profit >= threshold
        else if (/^(?:ap|avgp)\s*>\s*(\d+(\.\d+)?)/i.test(segment)) {
          const match = segment.match(/^(?:ap|avgp)\s*>\s*(\d+(\.\d+)?)/i)
          const threshold = parseFloat(match[1])
          tmp = tmp.filter(item => item.avg_profit >= threshold)
        }
        // Days Range filter: e.g., "10-40"
        else if (/^\d+\s*-\s*\d+$/i.test(segment)) {
          const parts = segment.split('-').map(s => parseInt(s.trim()))
          if (parts.length === 2) {
            const [n1, n2] = parts
            tmp = tmp.filter(item => item.daysOut >= n1 && item.daysOut <= n2)
          }
        }
        // TradeWave Average Profit filter: e.g., "TWA>10"
        else if (/^twa\s*>\s*(\d+(\.\d+)?)/i.test(segment)) {
          const match = segment.match(/^twa\s*>\s*(\d+(\.\d+)?)/i)
          const threshold = parseFloat(match[1])
          tmp = tmp.filter(item => item.avg_profit2 >= threshold)
        }
        // TradeWave Ratio filter: e.g., "TWR>2"
        else if (/^twr\s*>\s*(\d+(\.\d+)?)/i.test(segment)) {
          const match = segment.match(/^twr\s*>\s*(\d+(\.\d+)?)/i)
          const threshold = parseFloat(match[1])
          tmp = tmp.filter(item => item.sharpe_ratio2 >= threshold)
        }
        // Default text search across all columns
        else {
          tmp = tmp.filter(item =>
            JSON.stringify(item).toLowerCase().includes(lowerSegment)
          )
        }
      })
    }

    // Now apply the final sort
    if (colSorted !== '') {
      const colType = typeof table_data[0][colSorted]
      tmp.sort((a, b) => {
        let ret
        if (colType === 'number') {
          // numeric sort
          ret = sortedDir === 'a' ? a[colSorted] - b[colSorted] : b[colSorted] - a[colSorted]
        } else {
          // string (alphabetical) sort
          if (sortedDir === 'a') {
            ret =
              a[colSorted] < b[colSorted]
                ? -1
                : a[colSorted] > b[colSorted]
                  ? 1
                  : a[secondColSort] - b[secondColSort]
          } else {
            ret =
              a[colSorted] < b[colSorted]
                ? 1
                : a[colSorted] > b[colSorted]
                  ? -1
                  : a[secondColSort] - b[secondColSort]
          }
        }
        return ret
      })
    }

    // Update long/short counts
    const shortCount = tmp.filter(item => item.lOrS === 'Short').length
    const longCount = tmp.filter(item => item.lOrS === 'Long').length
    SetNumLongs(longCount)
    SetNumShorts(shortCount)

    // Update processed data & table length
    SetTableDataProcessed(tmp)
    SetOppTableLength(tmp.length)

    // Reset row selection logic if needed
    const decoded = jwt_decode(token)
    const exp = decoded['exp']
    const secs_now = Math.floor(Date.now() / 1000)
    const secs_since_token = -(secs_now, exp - secs_now - 10800)
    if (secs_since_token < 30 && loggedinUser === '0') {
      // do nothing
    } else {
      SetRowIndexClicked(null)
    }

    // Rebuild the column titles & tooltips
    let daysout_title = 'Days'
    if (rdd.isMobile && !rdd.isTablet && window.innerHeight > window.innerWidth) {
      daysout_title = 'Days'
    }

    const tmpDict = {}
    const tmpDict_tt = {}

    Object.keys(table_data[0]).forEach(k => {
      if (k === 'daysOut') {
        tmpDict['daysOut'] = daysout_title
        tmpDict_tt['daysOut'] = 'Number of calendar days to hold the security for the opportunity. Click to sort by Days-Hold'
      } else if (k === 'date') {
        tmpDict['date'] = 'Date'
        tmpDict_tt['date'] = 'Start date of the seasonal opportunity'
      } else if (k === 'symbol') {
        tmpDict['symbol'] = 'Ticker'
        tmpDict_tt['symbol'] = 'Ticker symbol for the opportunity. Click to sort by Ticker symbol'
      } else if (k === 'lOrS') {
        tmpDict['lOrS'] = 'DIR'
        tmpDict_tt['lOrS'] = 'Direction of the opportunity (Long/Short). Click to sort by direction'
      } else if (k === 'sharpe_ratio') {
        tmpDict['sharpe_ratio'] = 'SR'
        tmpDict_tt['sharpe_ratio'] = 'The Sharpe Ratio helps qualify the quality of the opportunity. Click to sort by Sharpe Ratio'
      } else if (k === 'median_profit') {
        tmpDict['median_profit'] = 'MP'
        tmpDict_tt['median_profit'] = 'The Median Profit indicates the middle value of strategy profits.'
      } else if (k === 'avg_profit') {
        tmpDict['avg_profit'] = 'AvgP'
        tmpDict_tt['avg_profit'] = 'The Average Profit represents the average of profits generated.'
      } else if (k === 'avg_profit2') {
        tmpDict['avg_profit2'] = 'TWA'
        tmpDict_tt['avg_profit2'] = 'TradeWave Average Profit calculated using MFE.'
      } else if (k === 'sharpe_ratio2') {
        tmpDict['sharpe_ratio2'] = 'TWR'
        tmpDict_tt['sharpe_ratio2'] = 'TradeWave Ratio based on MFE.'
      } else {
        tmpDict[k] = k
      }
    })

    SetTableTitleDict(tmpDict)
    SetTableTitleTooltip(tmpDict_tt)
  }, [sortedDir, colSorted, filterText, showAciveOpps])

  //-------------------------------------------------------------------------------------------------------------------
  const handleTitleClicked = title => () => {
    if (colSorted !== title) {
      SetColSorted(title)
      if (title === 'daysOut' || title === 'lOrS' || title === 'symbol') {
        SetSortedDir('a')
      } else {
        SetSortedDir('d')
      }
    } else {
      if (sortedDir === 'a') SetSortedDir('d')
      else SetSortedDir('a')
    }
  }

  //-------------------------------------------------------------------------------------------------------------------
  const tableStyle = {
    fontSize: tableTextSize
  }

  //-------------------------------------------------------------------------------------------------------------------
  const toggleUpgrade = () => {
    SetShowUpgradeBanner(!showUpgradeBanner)
  }

  //-------------------------------------------------------------------------------------------------------------------
  return (
    <div tabIndex="0" onKeyDown={handlerKeyDown}>
      <table className="table-striped" style={tableStyle} >

        {showSR2
          ? (
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
          )
          : (
            <colgroup>
              <col style={{ width: '27%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
          )
        }

        <thead>
          <tr style={{ fontSize: tableTitleTextSize, borderBottom: 'none' }}>
            {Object.keys(table_data[0]).map((title) => (
              <Tippy
                key={title}
                placement={'bottom'}
                content={
                  <div style={{ backgroundColor: 'red', color: 'white', paddingLeft: '3px', paddingRight: '3px' }}>
                    {tooltipSW ? tableTitleTooltip[title] : ''}
                  </div>
                }
              >
                <th
                  key={title}
                  style={{
                    height: rowHeight,
                    backgroundColor: title === colSorted ? 'rgb(220,220,220)' : 'rgb(192,192,192)',
                    color: showAciveOpps ? 'blue' : 'black'
                  }}
                  onClick={handleTitleClicked(title)}
                >
                  {tableTitleDict[title]}{' '}
                  {title === colSorted
                    ? (sortedDir === 'a' ? <BsChevronDown /> : <BsChevronUp />)
                    : <BsChevronExpand />}
                </th>
              </Tippy>
            ))}
          </tr>
        </thead>

        <tbody>
          {tableDataProcessed.map((row, index) => (
            <tr
              key={`row-${index}`}
              id={index}
              onClick={handlerRowClicked(index)}
              className={rowIndexClicked === index ? "selected" : "stripes"}
            >
              {Object.keys(row).map((key) => (
                <td key={`${key}-${index}`} style={{ height: rowHeight }}>
                  {row[key]}
                  {(key === 'avg_profit' || key === 'avg_profit2' || key === 'median_profit')
                    ? '%'
                    : ''}
                </td>
              ))}
            </tr>
          ))}

          {loggedinUser === '0' && (
            <tr>
              <td colSpan='6'>
                <div style={{ border: '4px dashed green', width: '100%', height: '20%', background: 'Honeydew' }}>
                  <span style={{ color: 'green', font: 'Arial', fontSize: '1em' }}>
                    <b>
                      <a href="/register/?lid=1" target="_blank" rel="noopener noreferrer">
                        Register
                      </a>
                      {' '}for a free account or{' '}
                      <a href="/member-login/" target="_blank" rel="noopener noreferrer">
                        login
                      </a>
                      {' '}to see all opportunities for today
                    </b>
                  </span>
                </div>
              </td>
            </tr>
          )}

          {/* desktop */}
          {showUpgradeBanner && wpUserLevels.length === 1 && wpUserLevels[0] === '1' && !rdd.isMobile && (
            <tr>
              <td colSpan='6' style={{ paddingTop: '20px' }}>
                <div
                  style={{
                    position: 'relative',
                    border: '4px dashed green',
                    width: '100%',
                    background: 'Honeydew',
                    padding: '15px',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  <div
                    onClick={toggleUpgrade}
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '20px',
                      lineHeight: '1',
                      margin: '0',
                      padding: '0',
                      borderRadius: '50%',
                      color: 'green',
                      backgroundColor: 'transparent'
                    }}
                  >
                    ✖
                  </div>

                  <div
                    style={{
                      color: 'green',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '1em',
                      maxWidth: '750px',
                      margin: '7px auto',
                      lineHeight: '1.5em'
                    }}
                  >
                    <b>{upgradeMessage}</b>
                  </div>
                  <div
                    style={{
                      marginTop: '10px',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <a
                      href="/my-account/?ihc_ap_menu=subscription"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: 'white',
                        backgroundColor: 'green',
                        padding: '10px 20px',
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        border: '2px solid white',
                        width: 'fit-content'
                      }}
                    >
                      Click Here to Upgrade Now!
                    </a>
                  </div>
                </div>

                {/* Promotion Banner */}
                {promotionMessage && (
                  <div
                    style={{
                      width: '100%',
                      padding: '15px',
                      background: promotionBackColor,
                      boxSizing: 'border-box',
                      textAlign: 'center',
                      fontFamily: 'Arial, sans-serif',
                      marginTop: '15px',
                      border: '4px dashed black'
                    }}
                  >
                    <div
                      style={{
                        color: 'white',
                        fontSize: '1.2em',
                        fontWeight: 'bold'
                      }}
                    >
                      {promotionMessage}
                    </div>
                    <div
                      style={{
                        backgroundColor: 'black',
                        color: 'yellow',
                        padding: '10px 20px',
                        borderRadius: '5px',
                        display: 'inline-block',
                        fontWeight: 'bold',
                        fontSize: '1.4em',
                        marginTop: '10px'
                      }}
                    >
                      Use Code: {promotionCouponCode}
                    </div>
                  </div>
                )}
              </td>
            </tr>
          )}

          {/* smartphone portrait */}
          {showUpgradeBanner && wpUserLevels.length === 1 && wpUserLevels[0] === '1' && rdd.isMobile && !rdd.isTablet && window.innerHeight > window.innerWidth && (
            <tr style={{ padding: '0', margin: '0', width: '100%' }}>
              <td colSpan='6' style={{ padding: '0', margin: '0', width: '100%' }}>
                <div
                  style={{
                    width: '100vw',
                    padding: '0',
                    margin: '0',
                    background: 'lightgreen',
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {/* Left Div: Upgrade Button */}
                  <div
                    style={{
                      width: promotionMessage !== '' ? '90%' : '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <a
                      href={`/cp/mobile-upgrade.html?upgradeMessage=${encodeURIComponent(upgradeMessage)}&promotionMessage=${encodeURIComponent(promotionMessage)}&promotionBackColor=${encodeURIComponent(promotionBackColor)}&promotionCouponCode=${encodeURIComponent(promotionCouponCode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: 'white',
                        backgroundColor: 'green',
                        padding: '15px 0',
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '100%',
                        fontSize: '1.2em',
                        margin: '0 auto',
                        boxSizing: 'border-box'
                      }}
                    >
                      Upgrade to Premium for Full Access
                    </a>
                  </div>

                  {/* Right Div: Gift Box */}
                  {promotionMessage !== '' && (
                    <div
                      style={{
                        width: '10%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <a
                        href={`/mobile-upgrade?upgradeMessage=${encodeURIComponent(upgradeMessage)}&promotionMessage=${encodeURIComponent(promotionMessage)}&promotionBackColor=${encodeURIComponent(promotionBackColor)}&promotionCouponCode=${encodeURIComponent(promotionCouponCode)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: 'white',
                          backgroundColor: 'red',
                          padding: '15px 0',
                          borderRadius: '5px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '100%',
                          fontSize: '1.6em',
                          margin: '0 auto',
                          boxSizing: 'border-box'
                        }}
                      >
                        🎁
                      </a>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default TableBox
